import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {RoleModel, SpecialRoleAliasesEnum} from '../../../../models/api/roleModel';

export interface RoleDropdownItemProps {
  key: SpecialRoleAliasesEnum | string;
  value: SpecialRoleAliasesEnum | string;
  name: ReactNode;
}

/** Builds list of roles that can be granted to invited teammates. */
export function buildRoleDropdownItems(
  roles: ReadonlyArray<RoleModel>,
): ReadonlyArray<RoleDropdownItemProps> {
  const customRoles = roles.filter((role) => !role.isSpecial);

  // We only want the Admin and Member special roles, plus any custom roles the
  // company has.
  return [
    {
      key: SpecialRoleAliasesEnum.ADMIN,
      value: SpecialRoleAliasesEnum.ADMIN,
      name: renderSpecialRoleName(SpecialRoleAliasesEnum.ADMIN),
    },
    {
      key: SpecialRoleAliasesEnum.MEMBER,
      value: SpecialRoleAliasesEnum.MEMBER,
      name: renderSpecialRoleName(SpecialRoleAliasesEnum.MEMBER),
    },
    ...customRoles.map((role) => ({
      key: role.alias,
      value: role.alias,
      name: renderRoleName(role),
    })),
  ];
}

function renderSpecialRoleName(roleAlias: string): ReactNode | undefined {
  if (roleAlias === SpecialRoleAliasesEnum.ADMIN) {
    return <Trans id="roleoption-admin">Admin</Trans>;
  }

  if (roleAlias === SpecialRoleAliasesEnum.MEMBER) {
    return <Trans id="roleoption-member">Member</Trans>;
  }

  return undefined;
}

function renderRoleName(role: RoleModel): ReactNode {
  if (role.isSpecial) {
    return renderSpecialRoleName(role.alias) ?? null;
  }

  return role.name;
}

interface RolesDropdownProps extends PopoverTransferProps {
  customRoles: ReadonlyArray<RoleModel>;
  onSelect: (roleAlias: string | null) => void;
  selectedRoleAlias?: string | null;
  isDisabled?: boolean;
  emptyItem?: EmptyRoleItem;
}
const RolesDropdown: FC<RolesDropdownProps> = (props) => {
  const {customRoles, selectedRoleAlias, onSelect, isDisabled, emptyItem, ...modalProps} = props;

  return (
    <SimpleDropdown {...modalProps}>
      {emptyItem ? (
        <SimpleDropdownItem
          isDisabled={isDisabled || emptyItem.isDisabled}
          renderTooltip={emptyItem.renderTooltip}
          isChecked={selectedRoleAlias === null}
          onSelect={() => onSelect(null)}
        >
          <Trans id="roleoption-noaccess">No access</Trans>
        </SimpleDropdownItem>
      ) : null}
      {buildRoleDropdownItems(customRoles).map((r) => (
        <SimpleDropdownItem
          key={r.key}
          isChecked={selectedRoleAlias === r.value}
          onSelect={() => onSelect(r.value)}
          isDisabled={isDisabled}
        >
          {r.name}
        </SimpleDropdownItem>
      ))}
    </SimpleDropdown>
  );
};

export function renderRoleValue(
  roleAlias: string | null | undefined,
  roles: ReadonlyArray<RoleModel>,
): ReactNode {
  if (roleAlias === undefined) {
    return null;
  }

  if (roleAlias === null) {
    return <Trans id="roleoption-noaccess">No access</Trans>;
  }

  return renderSpecialRoleName(roleAlias) ?? roles.find((role) => role.alias === roleAlias)?.name ?? null;
}

export interface EmptyRoleItem {
  isDisabled?: boolean;
  renderTooltip?: Renderer;
}

interface RolesDropdownBoxProps {
  className?: string;
  roleAlias?: string | null;
  roles: ReadonlyArray<RoleModel>;
  size?: VisualSizesEnum;
  label?: ReactNode;
  emptyItem?: EmptyRoleItem;
  isDisabled?: boolean;
  onSelect: (roleAlias: string | null) => void;
}
export const RolesDropdownBox: FC<RolesDropdownBoxProps> = (props) => {
  const {roleAlias, roles, onSelect, size, label, emptyItem, className, isDisabled} = props;

  return (
    <DropdownBoxCoordinator
      value={renderRoleValue(roleAlias, roles)}
      size={size}
      label={label}
      className={className}
      isDisabled={isDisabled}
      renderDropdown={(modalProps) => (
        <RolesDropdown
          {...modalProps}
          customRoles={roles}
          selectedRoleAlias={roleAlias}
          onSelect={(r) => onSelect(r ?? null)}
          emptyItem={emptyItem}
        />
      )}
    />
  );
};
